import React, { useEffect, useState } from "react";
import {
    fetchMaterialSizes,
    fetchMaterialTypes,
    updateMaterialSize,
    updateMaterialType,
} from "../../services/MaterialAPI";
import MaterialList from "./MaterialList";
import EditMaterialModal from "./EditMaterialModal";
import CreateMaterialTypeForm from "../materials/CreateMaterialTypeForm";
import CreateMaterialSizeForm from "../materials/CreateMaterialSizeForm";

import { BUTTON_LABELS, MODAL_CONTENT, MODAL_TITLES } from "../../constants/MaterialConstants";
import ManageMaterialInventoryForm from "../inventory/ManageMaterialInventoryForm";
import FormComponent from "../generic/FormComponent"; // Import constants

interface MaterialType {
    id: number;
    material_type: string;
    public: boolean;
}

interface MaterialSize {
    id: number;
    size: string;
}

interface ManageMaterialsFormProps {
}

const ManageMaterialsPropertyForm: React.FC<ManageMaterialsFormProps> = () => {
    const [ materialTypes, setMaterialTypes ] = useState<MaterialType[]>([]);
    const [ materialSizes, setMaterialSizes ] = useState<MaterialSize[]>([]);
    const [ isModalOpen, setIsModalOpen ] = useState(false);
    const [ isEditModal, setIsEditModal ] = useState(false); // New state to distinguish modal types
    const [ modalContent, setModalContent ] = useState<string | null>(null);
    const [ currentItem, setCurrentItem ] = useState<{
        id: number;
        label: string;
        type: "type" | "size";
    } | null>(null);

    // Fetch material types and sizes on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const types = await fetchMaterialTypes();
                const sizes = await fetchMaterialSizes();
                setMaterialTypes(types);
                setMaterialSizes(sizes);
            } catch (error) {
                console.error("Error fetching materials:", error);
            }
        };
        fetchData();
    }, [ isModalOpen ]);

    // Open modal for editing
    const openModal = (id: number, label: string, type: "type" | "size") => {
        setCurrentItem({ id, label, type });
        setModalContent(null); // Clear modal content to avoid conflict
        setIsEditModal(true); // Set to edit mode
        setIsModalOpen(true);
    };

    // Open modal for creating
    const openCreateModal = (content: string) => {
        setCurrentItem(null); // Reset current item
        setModalContent(content); // Set modal content
        setIsEditModal(false); // Set to create mode
        setIsModalOpen(true);
    };

    const handleModalSubmit = async (newValue: string) => {
        if (!currentItem) return;

        let responseData;
        if (currentItem.type === "type") {
            const updatedType = await updateMaterialType(currentItem.id, newValue);
            responseData = updatedType;

            setMaterialTypes((prev) =>
                prev.map((type) =>
                    type.id === currentItem.id
                        ? { ...type, material_type: updatedType.material_type }
                        : type,
                ),
            );
        } else {
            const updatedSize = await updateMaterialSize(currentItem.id, newValue);
            responseData = updatedSize;
            setMaterialSizes((prev) =>
                prev.map((size) =>
                    size.id === currentItem.id
                        ? {
                            ...size,
                            size: updatedSize.material_size, // Fallback to the original size
                        }
                        : size,
                ),
            );
        }
        return responseData;
    };

    // Close modal
    const handleModalClose = () => {
        setIsModalOpen(false);
        setCurrentItem(null);
        setModalContent(null); // Clear modal content
        setIsEditModal(false); // Reset edit mode
    };

    return (
        <FormComponent>
            {/* Add Material Buttons */}
            <div className="flex flex-row pb-6 space-x-4">
                <button className={"primary"}
                        onClick={() => openCreateModal(MODAL_CONTENT.MATERIAL_TYPE)}>{BUTTON_LABELS.ADD_MATERIAL_TYPE}</button>
                <button className={"primary"}
                        onClick={() => openCreateModal(MODAL_CONTENT.MATERIAL_SIZE)}>{BUTTON_LABELS.ADD_MATERIAL_SIZE}</button>
            </div>

            <div className="flex gap-8">
                {/* Material Types */}
                <MaterialList
                    title="Material Types"
                    items={materialTypes.map((type) => ({
                        id: type.id,
                        label: type.material_type,
                        public: type.public,
                    })) || []}
                    onEdit={(id, label) => openModal(id || 0, label, "type")}
                />

                {/* Material Sizes */}
                <MaterialList
                    title="Material Sizes"
                    items={materialSizes.map((size) => ({
                        id: size.id,
                        label: size.size,
                    })) || []}
                    onEdit={(id, label) => openModal(id || 0, label, "size")}
                />
            </div>
            <ManageMaterialInventoryForm/>

            {/* Modal rendering logic */}
            {isModalOpen && isEditModal && currentItem && (
                <EditMaterialModal
                    title={`Edit ${currentItem.type === "type" ? MODAL_TITLES.MATERIAL_TYPE : MODAL_TITLES.MATERIAL_SIZE}`}
                    currentValue={currentItem.label}
                    onClose={handleModalClose}
                    onSubmit={handleModalSubmit}
                />
            )}

            {/* Modal for adding material types and sizes */}
            {isModalOpen && !isEditModal && modalContent && (
                <div>
                    {modalContent === MODAL_CONTENT.MATERIAL_TYPE && (
                        <CreateMaterialTypeForm onCloseModal={handleModalClose}/>
                    )}
                    {modalContent === MODAL_CONTENT.MATERIAL_SIZE && (
                        <CreateMaterialSizeForm onCloseModal={handleModalClose}/>
                    )}
                </div>
            )}
        </FormComponent>
    );
};

export default ManageMaterialsPropertyForm;